import React, { useEffect, useState } from 'react';
import {
  hämtaOklaraMatchningar, hämtaArtikelLista, bekräftaMatchning,
  OklarMatchning, Artikel,
} from '../api/client';
import SokbarLista from '../components/SokbarLista';
import { TEMA } from '../theme';

const monoStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
};

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '18px 22px',
  marginBottom: '12px',
};

const sekundärKnapp: React.CSSProperties = {
  height: '36px',
  padding: '0 18px',
  borderRadius: '2px',
  background: 'transparent',
  border: `1px solid ${TEMA.border}`,
  color: TEMA.inkSoft,
  fontFamily: "'Inter', sans-serif",
  fontSize: '13.5px',
  cursor: 'pointer',
};

const textKnapp: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  fontSize: '12.5px',
  color: TEMA.inkFaint,
};

export default function Artikelmatchning() {
  const [rader, setRader] = useState<OklarMatchning[]>([]);
  const [artiklar, setArtiklar] = useState<Artikel[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  const [arbetarId, setArbetarId] = useState<string | null>(null);
  const [egetVal, setEgetVal] = useState<Record<string, string>>({});
  const [visaLista, setVisaLista] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([hämtaOklaraMatchningar(), hämtaArtikelLista()])
      .then(([o, a]) => { setRader(o.data); setArtiklar(a.data); setLaddar(false); })
      .catch(err => {
        console.error('Kunde inte ladda matchningarna:', err);
        setFel('Kunde inte ladda matchningarna just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }, []);

  async function spara(
    rad: OklarMatchning, artikel_nr: string | null, artikel_namn: string | null,
    nyÅrgång = false,
  ) {
    setArbetarId(rad.provning_id);
    try {
      await bekräftaMatchning(rad.provning_id, {
        artikel_nr: artikel_nr || undefined,
        artikel_namn: artikel_namn || undefined,
        avvaktar: !artikel_nr,
        ny_argang: nyÅrgång,
        // Alla provningar av samma vin avgörs på en gång.
        provning_ids: rad.provning_ids,
      });
      setRader(prev => prev.filter(r => r.provning_id !== rad.provning_id));
    } catch (err) {
      console.error('Kunde inte spara:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setArbetarId(null);
    }
  }

  if (laddar) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
    </div>
  );

  if (fel) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: '#D9695F', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink }}>Viner att stämma av</h1>
        <p style={{ margin: '10px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", maxWidth: '680px', lineHeight: 1.5 }}>
          Viner som provats men inte säkert kunnat kopplas till sortimentet. Rätt artikel
          behövs för att se vilka provningar som lett till order, och för underlaget inför besök.
        </p>
      </div>

      {rader.length === 0 && (
        <div style={{ ...kortStyle, textAlign: 'center', padding: '40px 22px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
            Alla provade viner är avstämda.
          </p>
        </div>
      )}

      {rader.map(rad => {
        const upptagen = arbetarId === rad.provning_id;
        const visaAlla = visaLista === rad.provning_id;
        const förslag = rad.kandidater[0];
        return (
          <div key={rad.provning_id} style={kortStyle}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
              <span style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink }}>{rad.vin_namn}</span>
              {rad.pris && <span style={{ ...monoStyle, fontSize: '12.5px', color: TEMA.inkSoft }}>{rad.pris} kr</span>}
            </div>
            <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
              {[
                rad.producent,
                rad.provning_ids.length > 1
                  ? `${rad.provning_ids.length} provningar hos ${rad.kunder.join(', ')}`
                  : rad.kunder[0],
                rad.senaste_datum,
              ].filter(Boolean).join(' · ')}
            </p>

            {förslag ? (
              <>
                <p style={{ margin: '0 0 8px', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                  {förslag.ny_årgång
                    ? `Är det här samma vin, men årgång ${rad.vin_årgång}?`
                    : 'Är det här samma vin?'}
                </p>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: '12px',
                  padding: '12px 14px', borderRadius: '4px', marginBottom: '12px',
                  background: TEMA.bg, border: `1px solid ${TEMA.border}`,
                }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '14.5px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>
                    {förslag.namn}
                  </span>
                  <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.inkFaint, flexShrink: 0 }}>{förslag.pris} kr</span>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => spara(rad, förslag.artikel_nr, förslag.namn, förslag.ny_årgång)}
                    disabled={upptagen}
                    style={{
                      height: '36px', padding: '0 20px', borderRadius: '2px',
                      background: TEMA.accent, color: TEMA.onAccent, border: `1px solid ${TEMA.accent}`,
                      fontFamily: "'Inter', sans-serif", fontSize: '13.5px', fontWeight: 500,
                      cursor: upptagen ? 'default' : 'pointer', opacity: upptagen ? 0.6 : 1,
                    }}
                  >
                    {upptagen ? 'Sparar...' : förslag.ny_årgång ? 'Ja, ny årgång av det vinet' : 'Ja, det är den'}
                  </button>
                  <button
                    onClick={() => spara(rad, null, null)}
                    disabled={upptagen}
                    style={sekundärKnapp}
                  >
                    Nej, nytt vin
                  </button>
                  <button
                    onClick={() => setVisaLista(visaAlla ? null : rad.provning_id)}
                    style={{ ...textKnapp, marginLeft: 'auto' }}
                  >
                    {visaAlla ? 'Dölj' : 'Nej, välj ett annat'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <p style={{ margin: '0 12px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                  Vi hittar inget liknande vin i sortimentet.
                </p>
                <button onClick={() => spara(rad, null, null)} disabled={upptagen} style={{
                  height: '36px', padding: '0 20px', borderRadius: '2px',
                  background: TEMA.accent, color: TEMA.onAccent, border: `1px solid ${TEMA.accent}`,
                  fontFamily: "'Inter', sans-serif", fontSize: '13.5px', fontWeight: 500,
                  cursor: upptagen ? 'default' : 'pointer', opacity: upptagen ? 0.6 : 1,
                }}>
                  {upptagen ? 'Sparar...' : 'Nytt vin, inte upplagt än'}
                </button>
                <button
                  onClick={() => setVisaLista(visaAlla ? null : rad.provning_id)}
                  style={{ ...textKnapp, marginLeft: 'auto' }}
                >
                  {visaAlla ? 'Dölj' : 'Välj artikel själv'}
                </button>
              </div>
            )}

            {visaAlla && (
              <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${TEMA.border}` }}>
                {rad.kandidater.slice(1).map(k => (
                  <button
                    key={k.artikel_nr}
                    onClick={() => spara(rad, k.artikel_nr, k.namn, k.ny_årgång)}
                    disabled={upptagen}
                    style={{
                      display: 'flex', alignItems: 'baseline', gap: '12px', width: '100%', textAlign: 'left',
                      padding: '9px 12px', marginBottom: '6px', borderRadius: '4px',
                      background: 'transparent', border: `1px solid ${TEMA.border}`,
                      cursor: upptagen ? 'default' : 'pointer',
                      fontFamily: "'Inter', sans-serif", fontSize: '13.5px', color: TEMA.inkSoft,
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>{k.namn}</span>
                    <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.inkFaint }}>{k.pris} kr</span>
                  </button>
                ))}
                <SokbarLista
                  värde={egetVal[rad.provning_id] || ''}
                  onChange={nr => {
                    setEgetVal(v => ({ ...v, [rad.provning_id]: nr }));
                    const artikel = artiklar.find(a => a.artikel_nr === nr);
                    if (artikel) spara(rad, artikel.artikel_nr, artikel.namn);
                  }}
                  placeholder="Sök i hela sortimentet..."
                  alternativ={artiklar.map(a => ({ värde: a.artikel_nr, etikett: a.namn, under: `${a.pris} kr` }))}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
